/* benvel-variant-picker.js — Story 4.4
 *
 * <benvel-variant-picker> custom element.
 *
 * Responsibilities:
 *   1. Parse the inline <script type="application/json" data-variants> blob.
 *   2. Resolve the currently-selected variant from the checked radios.
 *   3. On each `change`, recompute which option values remain available
 *      given the OTHER options' current selection — set `disabled` on the
 *      non-current-option radios accordingly. Honest-stock posture (FR9 +
 *      NFR23): visible-disabled, never hidden.
 *   4. Update the URL via history.replaceState (NOT pushState — preserves
 *      the back button) preserving other query params.
 *   5. Toggle the sold-out announcement element's `hidden` attr.
 *   6. Sync the hidden <input name="id"> value for the buy-buttons form
 *      (architecture D1 — no new <form>, no native form-POST loss).
 *   7. Emit `benvel:variant-changed` (project-context §24 — `benvel:*`
 *      namespace; Horizon's `theme:variant-changed` is NOT emitted).
 *
 * No global writes. No setTimeout / setInterval. No third-party imports.
 * ES2020 baseline matches Horizon's JS budget.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('benvel-variant-picker')) return;

  class BenvelVariantPicker extends HTMLElement {
    constructor() {
      super();
      this._variants = [];
      this._optionNames = [];
      this._previousVariantId = this.getAttribute('data-current-variant-id') || null;
      this._handleChange = this._handleChange.bind(this);
      this._handlePopstate = this._handlePopstate.bind(this);
    }

    connectedCallback() {
      const blob = this.querySelector('script[data-variants]');
      if (!blob) return;
      try {
        this._variants = JSON.parse(blob.textContent || '[]');
      } catch (_e) {
        this._variants = [];
      }
      if (!Array.isArray(this._variants) || this._variants.length === 0) return;

      const fieldsets = this.querySelectorAll('fieldset[data-option-name]');
      this._optionNames = Array.from(fieldsets).map(function (fs) {
        return fs.getAttribute('data-option-name');
      });

      this.addEventListener('change', this._handleChange);
      window.addEventListener('popstate', this._handlePopstate);

      // Honor URL ?variant=<id> on initial load, only if DOM hasn't already
      // selected the same variant. DOM is the source of truth per D3 — but
      // bookmarked share links should win on first paint when valid.
      const urlVariantId = this._readVariantIdFromUrl();
      if (urlVariantId) {
        const target = this._variants.find(function (v) { return String(v.id) === String(urlVariantId); });
        if (target && String(target.id) !== this.getAttribute('data-current-variant-id')) {
          this._applyVariantSelection(target);
        }
      }

      // Initial emit so listeners (gallery, price, ATC) pick up the resolved variant.
      const initial = this._resolveCurrentVariant();
      this._recomputeDisabledStates(initial);
      this._syncDom(initial);
      this._dispatchVariantChanged(initial);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._handleChange);
      window.removeEventListener('popstate', this._handlePopstate);
    }

    _handleChange(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;
      const variant = this._resolveCurrentVariant();
      this._recomputeDisabledStates(variant);
      this._syncDom(variant);
      this._syncUrl(variant);
      this._dispatchVariantChanged(variant);
    }

    _handlePopstate() {
      const id = this._readVariantIdFromUrl();
      if (!id) return;
      const target = this._variants.find(function (v) { return String(v.id) === String(id); });
      if (!target) return;
      this._applyVariantSelection(target);
      const variant = this._resolveCurrentVariant();
      this._recomputeDisabledStates(variant);
      this._syncDom(variant);
      this._dispatchVariantChanged(variant);
    }

    _readVariantIdFromUrl() {
      try {
        const params = new URL(window.location.href).searchParams;
        return params.get('variant');
      } catch (_e) {
        return null;
      }
    }

    _getCheckedValues() {
      const checked = {};
      const inputs = this.querySelectorAll('input[type="radio"]:checked');
      inputs.forEach(function (input) {
        const name = input.getAttribute('data-option-name');
        if (name) checked[name] = input.value;
      });
      return checked;
    }

    _resolveCurrentVariant() {
      const checked = this._getCheckedValues();
      const optionNames = this._optionNames;
      const match = this._variants.find(function (v) {
        if (!v || !Array.isArray(v.options)) return false;
        return optionNames.every(function (name, idx) {
          return v.options[idx] === checked[name];
        });
      });
      return match || null;
    }

    _applyVariantSelection(variant) {
      if (!variant || !Array.isArray(variant.options)) return;
      const optionNames = this._optionNames;
      optionNames.forEach(function (name, idx) {
        const value = variant.options[idx];
        const radio = this.querySelector(
          'input[type="radio"][data-option-name="' + (window.CSS && CSS.escape ? CSS.escape(name) : name) + '"][value="' + (window.CSS && CSS.escape ? CSS.escape(value) : value) + '"]'
        );
        if (radio && !radio.disabled) radio.checked = true;
      }.bind(this));
    }

    _recomputeDisabledStates(currentVariant) {
      const checked = this._getCheckedValues();
      const optionNames = this._optionNames;
      const variants = this._variants;

      // For each option, for each value, determine whether ≥ 1 variant
      // with the OTHER options matching `checked` is available. If none,
      // disable that radio.
      this.querySelectorAll('fieldset[data-option-name]').forEach(function (fieldset) {
        const optionName = fieldset.getAttribute('data-option-name');
        const optionIdx = optionNames.indexOf(optionName);
        if (optionIdx < 0) return;

        fieldset.querySelectorAll('input[type="radio"]').forEach(function (input) {
          const value = input.value;
          let hasAvailable = false;
          for (let i = 0; i < variants.length; i++) {
            const v = variants[i];
            if (!v || !Array.isArray(v.options)) continue;
            if (v.options[optionIdx] !== value) continue;
            // Match the OTHER options against `checked` (if a value is checked).
            let othersMatch = true;
            for (let j = 0; j < optionNames.length; j++) {
              if (j === optionIdx) continue;
              const otherName = optionNames[j];
              if (checked[otherName] && v.options[j] !== checked[otherName]) {
                othersMatch = false;
                break;
              }
            }
            if (!othersMatch) continue;
            if (v.available) { hasAvailable = true; break; }
          }
          // Never disable the currently-checked radio — buyer needs to see
          // the sold-out announcement instead.
          if (input.checked) {
            input.disabled = false;
            return;
          }
          input.disabled = !hasAvailable;
        });
      });
    }

    _syncDom(variant) {
      const previousVariantId = this.getAttribute('data-current-variant-id') || null;

      // data-current-variant-id
      if (variant && variant.id != null) {
        this.setAttribute('data-current-variant-id', String(variant.id));
      } else {
        this.removeAttribute('data-current-variant-id');
      }

      // Hidden id input.
      const hidden = this.querySelector('input[data-benvel-variant-id-input]');
      if (hidden && variant && variant.id != null) {
        hidden.value = String(variant.id);
      }

      const formHidden = this._findProductFormVariantInput();
      if (formHidden && variant && variant.id != null) {
        formHidden.value = String(variant.id);
      }

      // Sold-out announcement toggle.
      const announce = this.querySelector('[data-benvel-sold-out-announce]');
      if (announce) {
        const soldOut = variant ? variant.available === false : false;
        if (soldOut) announce.removeAttribute('hidden');
        else announce.setAttribute('hidden', '');
      }

      this._syncColorLegend();
      this._syncSizeLegend();
      this._syncLowStockMessage();

      this._previousVariantId = previousVariantId;
    }

    // Toggle + recompose the visible low-stock line ("Solo quedan pocas unidades
    // en talla {size}.") for the selected size. The variant JSON does NOT serialize
    // inventory_quantity / inventory_policy, so the honest-stock signal can't be
    // recomputed client-side — instead we read the per-size dot the server already
    // rendered (same signal that gates the dot in Liquid), keeping server + client
    // in agreement. No-op when the product has no size option.
    _syncLowStockMessage() {
      const el = this.querySelector('[data-benvel-low-stock-message]');
      if (!el) return;
      const sizeInput = this.querySelector(
        'fieldset.benvel-variant-picker__fieldset--size input[type="radio"]:checked'
      );
      const label = sizeInput ? sizeInput.closest('label') : null;
      const isLow = !!(label && label.querySelector('.benvel-variant-picker__low-stock-dot'));
      if (!isLow || !sizeInput) {
        el.setAttribute('hidden', '');
        return;
      }
      const template = el.getAttribute('data-low-stock-template') || '';
      el.textContent = template.replace('%SIZE%', sizeInput.value);
      el.removeAttribute('hidden');
    }

    // Recompose the color legend "Color · {selected}" from the localized
    // prefix Liquid emitted on the legend. No-op when the product has no
    // color option. Pure string substitution — no locale re-fetch.
    _syncColorLegend() {
      const legend = this.querySelector('[data-benvel-color-legend]');
      if (!legend) return;
      const colorInput = this.querySelector(
        'fieldset.benvel-variant-picker__fieldset--color input[type="radio"]:checked'
      );
      if (!colorInput) return;
      const prefix = legend.getAttribute('data-color-label-prefix') || '';
      legend.textContent = prefix + colorInput.value;
    }

    // Recompose the size legend "Talla · {selected}" — mirror of the color
    // legend. The target is an inner <span> (not the <legend> itself) so the
    // adjacent size-guide trigger anchor is never clobbered. No-op when the
    // product has no size option.
    _syncSizeLegend() {
      const target = this.querySelector('[data-benvel-size-legend]');
      if (!target) return;
      const sizeInput = this.querySelector(
        'fieldset.benvel-variant-picker__fieldset--size input[type="radio"]:checked'
      );
      if (!sizeInput) return;
      const prefix = target.getAttribute('data-size-label-prefix') || '';
      target.textContent = prefix + sizeInput.value;
    }

    _findProductFormVariantInput() {
      const productId = this.getAttribute('data-product-id');
      const section = this.closest('.shopify-section');
      if (!productId || !section) return null;
      return section.querySelector(
        'product-form-component[data-product-id="' + productId + '"] input[name="id"][ref="variantId"]'
      );
    }

    _syncUrl(variant) {
      if (!variant || variant.id == null) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      } catch (_e) { /* noop */ }
    }

    _dispatchVariantChanged(variant) {
      const optionValues = this._getCheckedValues();
      const available = variant ? variant.available !== false : false;
      const detail = {
        variant: variant,
        variantId: variant && variant.id != null ? variant.id : null,
        previousVariantId: this._previousVariantId,
        product: this.getAttribute('data-product-id'),
        available: available,
        soldOut: !available,
        optionValues: optionValues,
        source: 'benvel-variant-picker'
      };
      this.dispatchEvent(new CustomEvent('benvel:variant-changed', {
        bubbles: true,
        composed: true,
        detail: detail
      }));
    }
  }

  customElements.define('benvel-variant-picker', BenvelVariantPicker);
})();
